import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircle,
  Send,
  Trophy,
  Sparkles,
  Calendar,
  MapPin,
  Building,
  Users,
  BarChart3,
  Package,
  Target,
  Mail,
  Clock,
  Star,
  Rocket,
  Shield,
  TrendingUp,
  AlertTriangle,
  Globe,
  Edit3,
  Factory,
  Database,
  FileText,
  Upload
} from 'lucide-react'
import { useOnboardingStore } from '../../../stores/onboardingStore'
import { useToastStore } from '../../../stores/toastStore'
import { getIndustryConfig } from '../industry-configs'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Badge } from '../../../components/ui/Badge'
import { HelpTooltip } from '../../../components/ui/Tooltip'
import { IntelligenceProfileService } from '../../../services/intelligenceProfileService'
import { cn } from '../../../lib/utils'

export const IntelligenceProfileSummaryScreen: React.FC = () => {
  const { data } = useOnboardingStore()
  const { addToast } = useToastStore()
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!data.selectedIndustry) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Complete Onboarding First</h3>
          <p className="text-gray-500">Please complete the onboarding process to view your intelligence profile.</p>
        </div>
      </div>
    )
  }
  
  const industryConfig = getIndustryConfig(data.selectedIndustry)

  const handleComplete = async () => {
    setIsSubmitting(true)
    
    try {
      const result = await IntelligenceProfileService.processCompletion(data, industryConfig)
      
      if (result.success) {
        // Show success toast
        addToast({
          type: 'success',
          title: 'Intelligence Profile Activated!',
          description: 'Your strategic intelligence monitoring is now active. Redirecting to payment setup...',
          duration: 3000
        })
        
        // Redirect to payment page after a short delay
        setTimeout(() => {
          navigate('/payment')
        }, 2000)
      } else {
        // Show warning toast for offline mode
        addToast({
          type: 'warning',
          title: 'Profile Saved Locally',
          description: 'Your profile has been saved and will sync when connection is restored. Proceeding to payment...',
          duration: 5000,
          action: {
            label: 'Continue to Payment',
            onClick: () => navigate('/payment')
          }
        })
        
        // Still redirect to payment page after a longer delay
        setTimeout(() => {
          navigate('/payment')
        }, 4000)
      }
      
    } catch (error) {
      console.error('Completion process failed:', error)
      
      // Show error toast
      addToast({
        type: 'error',
        title: 'Activation Failed',
        description: 'An error occurred while processing your profile. Please try again or contact support.',
        duration: 7000,
        action: {
          label: 'Contact Support',
          onClick: () => window.open('mailto:support@stratiqx.com', '_blank')
        }
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Calculate completion statistics - UPDATED to include Data Integration
  const getProfileStats = () => {
    const hasBusinessProfile = data.contactInfo.companyName && data.businessProfile.businessType
    const hasRevenueStreams = data.revenueStreams.length > 0
    const hasSuppliers = data.suppliers.length > 0
    const hasCompetitive = data.competitiveProfile.serviceArea.city && data.competitiveProfile.serviceArea.state
    const hasDataIntegration = data.uploadedFiles.length > 0 || data.additionalNotes?.length > 0
    const hasDelivery = data.deliveryPreferences.frequency && data.deliveryPreferences.format.length > 0
    
    const completedSections = [
      hasBusinessProfile, 
      hasRevenueStreams, 
      hasSuppliers, 
      hasCompetitive, 
      hasDataIntegration,
      hasDelivery
    ].filter(Boolean).length
    
    const totalSections = 6 // Updated from 5 to 6
    
    return {
      completedSections,
      totalSections,
      completionPercentage: Math.round((completedSections / totalSections) * 100),
      isComplete: completedSections === totalSections,
      hasDataIntegration
    }
  }

  const stats = getProfileStats()

  // Get estimated value based on configuration completeness
  const getEstimatedValue = () => {
    const baseValue = stats.completionPercentage * 50 // Base on completion
    const revenueBonus = data.revenueStreams.length * 100
    const supplierBonus = data.suppliers.length * 75
    const competitorBonus = data.competitiveProfile.knownCompetitors.length * 25
    const uploadBonus = data.uploadedFiles.length * 150
    
    return Math.round(baseValue + revenueBonus + supplierBonus + competitorBonus + uploadBonus)
  }

  const estimatedValue = getEstimatedValue()

  const formatFrequency = (freq: string) => {
    switch (freq) {
      case 'monthly': return 'Monthly Reports'
      case 'bi-weekly': return 'Bi-weekly Reports'
      case 'weekly-plus-monthly': return 'Weekly Alerts + Monthly Deep Dive'
      default: return 'Not configured'
    }
  }

  const getDeliveryFormats = () => {
    const formatMap: { [key: string]: string } = {
      'email-pdf': 'Email PDF',
      'web-dashboard': 'Web Dashboard',
      'email-alerts': 'Email Alerts',
      'phone-briefing': 'Phone Briefing'
    }
    return data.deliveryPreferences.format.map(f => formatMap[f] || f).join(', ')
  }

  // Format industry name for display
  const getIndustryDisplayName = () => {
    switch (data.selectedIndustry) {
      case 'automotive-services': return 'Automotive Services'
      case 'retail': return 'Retail'
      case 'healthcare': return 'Healthcare'
      case 'manufacturing': return 'Manufacturing'
      case 'hospitality': return 'Hospitality'
      case 'real-estate': return 'Real Estate'
      case 'technology': return 'Technology'
      case 'financial-services': return 'Financial Services'
      default: return data.selectedIndustry.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
  }

  // Format file size for display
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-3 mb-6 px-6 py-3 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full">
          <div className="w-8 h-8 bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg flex items-center justify-center">
            <Trophy className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold bg-gradient-to-r from-green-700 to-emerald-700 bg-clip-text text-transparent">
            Configuration Complete
          </span>
        </div>
        
        <h2 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
          Your Strategic Intelligence Profile
          <HelpTooltip text="Review your complete intelligence configuration before activation. This summary shows all the areas we'll monitor and how insights will be delivered to optimize your business decisions." />
        </h2>
        <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
          Congratulations! Your personalized intelligence monitoring system is configured and ready 
          to deliver actionable insights tailored to your business needs.
        </p>

        {/* Achievement Badge */}
        <div className="mt-8 inline-flex items-center gap-4 bg-white rounded-2xl p-6 border-2 border-green-200 shadow-lg">
          <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <div className="text-left">
            <div className="text-2xl font-bold text-gray-900">{stats.completionPercentage}% Complete</div>
            <div className="text-gray-600">
              {stats.completedSections} of {stats.totalSections} configuration sections completed
            </div>
            <div className="text-sm font-medium text-green-600 mt-1">
              Estimated Monthly Value: ${estimatedValue.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Industry & Company Overview */}
      <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Building className="w-5 h-5 text-white" />
            </div>
            Company & Industry Profile
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Industry Badge - NEW ADDITION */}
          <div className="mb-6 p-4 bg-white rounded-xl border border-blue-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center">
                <Factory className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-sm font-medium text-blue-700">Industry Sector</div>
                <div className="text-xl font-bold text-blue-900">{getIndustryDisplayName()}</div>
                <div className="text-sm text-blue-600 mt-1">
                  Intelligence configured for {industryConfig.name} industry standards
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-blue-700">
                <Building className="w-4 h-4" />
                <span className="font-medium">Company</span>
              </div>
              <div>
                <div className="font-semibold text-blue-900">{data.contactInfo.companyName || 'Not specified'}</div>
                <div className="text-sm text-blue-700">
                  {industryConfig.businessTypes.find(t => t.id === data.businessProfile.businessType)?.name || 'Business type not selected'}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-blue-700">
                <MapPin className="w-4 h-4" />
                <span className="font-medium">Location</span>
              </div>
              <div>
                <div className="font-semibold text-blue-900">
                  {data.businessProfile.location.city && data.businessProfile.location.state 
                    ? `${data.businessProfile.location.city}, ${data.businessProfile.location.state}`
                    : 'Not specified'}
                </div>
                <div className="text-sm text-blue-700">
                  {data.businessProfile.location.serviceRadius 
                    ? `${data.businessProfile.location.serviceRadius} service radius`
                    : 'Service area not defined'}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-blue-700">
                <Users className="w-4 h-4" />
                <span className="font-medium">Scale</span>
              </div>
              <div>
                <div className="font-semibold text-blue-900">
                  {data.businessProfile.companySize || 'Not specified'}
                </div>
                <div className="text-sm text-blue-700">
                  {data.businessProfile.annualRevenue || 'Revenue not specified'}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-blue-700">
                <Mail className="w-4 h-4" />
                <span className="font-medium">Contact</span>
              </div>
              <div>
                <div className="font-semibold text-blue-900">{data.contactInfo.contactPerson || 'Not specified'}</div>
                <div className="text-sm text-blue-700 truncate">{data.contactInfo.email || 'Email not provided'}</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Intelligence Configuration Overview - First Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Intelligence */}
        <Card className="border-2 border-emerald-200 hover:border-emerald-300 transition-colors">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-emerald-600" />
              </div>
              Revenue Intelligence
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.revenueStreams.length > 0 ? (
              <div className="space-y-3">
                {data.revenueStreams.map((stream, index) => {
                  const streamConfig = industryConfig.revenueStreamTypes.find(t => t.id === stream.type)
                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                      <div>
                        <div className="font-medium text-emerald-800">{streamConfig?.name}</div>
                        <div className="text-sm text-emerald-600">
                          {stream.partners && stream.partners.length > 0 && `${stream.partners.length} partners monitored`}
                        </div>
                      </div>
                      <div className="font-bold text-emerald-700">{stream.percentage}%</div>
                    </div>
                  )
                })}
                <div className="mt-4 p-3 bg-emerald-100 rounded-lg">
                  <div className="text-sm font-medium text-emerald-800">
                    ✓ Monitoring {data.revenueStreams.length} revenue stream{data.revenueStreams.length > 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <BarChart3 className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <div>No revenue streams configured</div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Supply Chain Intelligence */}
        <Card className="border-2 border-orange-200 hover:border-orange-300 transition-colors">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <Package className="w-4 h-4 text-orange-600" />
              </div>
              Supply Chain Intelligence
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.suppliers.length > 0 ? (
              <div className="space-y-3">
                {data.suppliers.map((supplier, index) => {
                  const categoryConfig = industryConfig.supplierCategories.find(c => c.id === supplier.category)
                  return (
                    <div key={index} className="p-3 bg-orange-50 rounded-lg">
                      <div className="font-medium text-orange-800">{categoryConfig?.name}</div>
                      <div className="text-sm text-orange-600">
                        Primary: {supplier.primarySupplier || 'TBD'}
                        {supplier.monthlySpend && ` • ${supplier.monthlySpend}`}
                      </div>
                      {supplier.alternativeSuppliers && supplier.alternativeSuppliers.length > 0 && (
                        <div className="text-xs text-orange-600 mt-1">
                          +{supplier.alternativeSuppliers.length} backup options
                        </div>
                      )}
                    </div>
                  )
                })}
                <div className="mt-4 p-3 bg-orange-100 rounded-lg">
                  <div className="text-sm font-medium text-orange-800">
                    ✓ Monitoring {data.suppliers.length} supplier categor{data.suppliers.length > 1 ? 'ies' : 'y'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <Package className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <div>No suppliers configured</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Intelligence Configuration Overview - Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Competitive Intelligence */}
        <Card className="border-2 border-purple-200 hover:border-purple-300 transition-colors">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <Target className="w-4 h-4 text-purple-600" />
              </div>
              Competitive Intelligence
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-3 bg-purple-50 rounded-lg">
                <div className="font-medium text-purple-800 mb-2">Market Area</div>
                <div className="text-sm text-purple-600">
                  {data.competitiveProfile.serviceArea.city && data.competitiveProfile.serviceArea.state 
                    ? `${data.competitiveProfile.serviceArea.city}, ${data.competitiveProfile.serviceArea.state}`
                    : 'Not specified'}
                  {data.competitiveProfile.serviceArea.radius && (
                    <span> • {data.competitiveProfile.serviceArea.radius} radius</span>
                  )}
                </div>
              </div>

              {data.competitiveProfile.competitiveAdvantages.length > 0 && (
                <div className="p-3 bg-purple-50 rounded-lg">
                  <div className="font-medium text-purple-800 mb-2">Key Advantages</div>
                  <div className="text-sm text-purple-600">
                    {data.competitiveProfile.competitiveAdvantages.slice(0, 3).join(', ')}
                    {data.competitiveProfile.competitiveAdvantages.length > 3 && 
                      ` +${data.competitiveProfile.competitiveAdvantages.length - 3} more`}
                  </div>
                </div>
              )}

              {data.competitiveProfile.knownCompetitors.length > 0 && (
                <div className="p-3 bg-purple-50 rounded-lg">
                  <div className="font-medium text-purple-800 mb-2">Direct Competitors</div>
                  <div className="text-sm text-purple-600">
                    Monitoring {data.competitiveProfile.knownCompetitors.length} specific competitor{data.competitiveProfile.knownCompetitors.length > 1 ? 's' : ''}
                  </div>
                </div>
              )}

              <div className="p-3 bg-purple-100 rounded-lg">
                <div className="text-sm font-medium text-purple-800">
                  ✓ Competitive monitoring active
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Integration - NEW SECTION */}
        <Card className="border-2 border-indigo-200 hover:border-indigo-300 transition-colors">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3">
              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Database className="w-4 h-4 text-indigo-600" />
              </div>
              Data Integration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.uploadedFiles.length > 0 ? (
                <>
                  <div className="p-3 bg-indigo-50 rounded-lg">
                    <div className="font-medium text-indigo-800 mb-2">Uploaded Documents</div>
                    <div className="space-y-2">
                      {data.uploadedFiles.slice(0, 3).map((file, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-indigo-600">
                          <FileText className="w-4 h-4" />
                          <span className="truncate flex-1">{file.name}</span>
                          <span className="text-xs text-indigo-500">
                            {formatFileSize(file.size)}
                          </span>
                        </div>
                      ))}
                      {data.uploadedFiles.length > 3 && (
                        <div className="text-xs text-indigo-600 font-medium">
                          +{data.uploadedFiles.length - 3} more file{data.uploadedFiles.length - 3 > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="p-3 bg-indigo-100 rounded-lg">
                    <div className="text-sm font-medium text-indigo-800">
                      ✓ {data.uploadedFiles.length} document{data.uploadedFiles.length > 1 ? 's' : ''} ready for analysis
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {data.additionalNotes && data.additionalNotes.length > 0 ? (
                    <>
                      <div className="p-3 bg-indigo-50 rounded-lg">
                        <div className="font-medium text-indigo-800 mb-2">Additional Context</div>
                        <div className="text-sm text-indigo-600">
                          Custom requirements provided for intelligence analysis
                        </div>
                      </div>
                      <div className="p-3 bg-indigo-100 rounded-lg">
                        <div className="text-sm font-medium text-indigo-800">
                          ✓ Context notes included
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <div>No documents uploaded</div>
                      <div className="text-sm text-gray-400 mt-1">Optional - can be added later</div>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Delivery Preferences - Now standalone full width */}
      <Card className="border-2 border-teal-200 hover:border-teal-300 transition-colors">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-3">
            <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-4 h-4 text-teal-600" />
            </div>
            Delivery Preferences
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-3 bg-teal-50 rounded-lg">
              <div className="font-medium text-teal-800 mb-1">Report Schedule</div>
              <div className="text-sm text-teal-600">
                {formatFrequency(data.deliveryPreferences.frequency)}
              </div>
            </div>

            {data.deliveryPreferences.format.length > 0 && (
              <div className="p-3 bg-teal-50 rounded-lg">
                <div className="font-medium text-teal-800 mb-1">Delivery Formats</div>
                <div className="text-sm text-teal-600">
                  {getDeliveryFormats()}
                </div>
              </div>
            )}

            <div className="p-3 bg-teal-50 rounded-lg">
              <div className="font-medium text-teal-800 mb-1">Alert Threshold</div>
              <div className="text-sm text-teal-600">
                {data.deliveryPreferences.alertThreshold === 'major-only' 
                  ? 'Major changes only' 
                  : 'All relevant updates'}
              </div>
            </div>

            {data.deliveryPreferences.priorities.length > 0 && (
              <div className="p-3 bg-teal-50 rounded-lg">
                <div className="font-medium text-teal-800 mb-1">Intelligence Priorities</div>
                <div className="text-sm text-teal-600">
                  {data.deliveryPreferences.priorities.length} focus area{data.deliveryPreferences.priorities.length > 1 ? 's' : ''} selected
                </div>
              </div>
            )}
          </div>
          
          <div className="mt-4 p-3 bg-teal-100 rounded-lg">
            <div className="text-sm font-medium text-teal-800 text-center">
              ✓ Delivery preferences configured
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Intelligence Value Proposition */}
      <Card className="border-2 border-gradient-to-r from-green-200 to-emerald-200 bg-gradient-to-br from-green-50 to-emerald-50">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            Your Intelligence Advantage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                <Clock className="w-8 h-8 text-green-600" />
              </div>
              <div className="font-semibold text-green-800">Early Warning System</div>
              <div className="text-sm text-green-700 mt-1">
                Get alerts 30-90 days before market changes impact your business
              </div>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                <Target className="w-8 h-8 text-green-600" />
              </div>
              <div className="font-semibold text-green-800">Personalized Intelligence</div>
              <div className="text-sm text-green-700 mt-1">
                Customized insights based on your specific business configuration
              </div>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                <Rocket className="w-8 h-8 text-green-600" />
              </div>
              <div className="font-semibold text-green-800">Competitive Edge</div>
              <div className="text-sm text-green-700 mt-1">
                Stay ahead of competitors with real-time market intelligence
              </div>
            </div>
          </div>

          <div className="p-4 bg-white rounded-xl border border-green-200 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <div className="font-semibold text-green-800 mb-2">What Happens Next?</div>
                <div className="space-y-2 text-sm text-green-700">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Your intelligence profile is processed within 24 hours</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>First intelligence report delivered within 5 business days</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Ongoing monitoring begins immediately for your configured areas</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Access to web dashboard and analyst support included</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <div className="text-sm text-green-700 mb-4">
              Estimated Monthly Intelligence Value: <strong>${estimatedValue.toLocaleString()}</strong>
            </div>
            <Button
              onClick={handleComplete}
              disabled={isSubmitting}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-4 text-lg font-semibold flex items-center gap-3 mx-auto"
              size="lg"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Activate Intelligence Monitoring
                </>
              )}
            </Button>
            
            {!stats.isComplete && (
              <div className="mt-4 text-sm text-amber-600 flex items-center justify-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Some sections incomplete - you can still activate and refine later
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Data Privacy Notice */}
      <Card className="border border-gray-200 bg-gray-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <Shield className="w-6 h-6 text-gray-600 mt-1" />
            <div>
              <div className="font-semibold text-gray-800 mb-2">Data Privacy & Security</div>
              <div className="text-sm text-gray-600 space-y-1">
                <div>• All business data is encrypted and processed securely</div>
                <div>• Uploaded files are analyzed and permanently deleted within 24 hours</div>
                <div>• Intelligence parameters extracted, never raw business data stored</div>
                <div>• Full compliance with SOC 2 Type II and GDPR standards</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}